
class NavimowPagination {
  constructor(config) {
    // 外部传入的分页容器与初始状态。
    this.container = config.container;
    this.productGrid = config.productGrid;
    this.labels = config.labels || {};
    this.totalPages = Math.max(0, config.totalPages || 0);
    this.currentPage = Math.min(
      Math.max(config.currentPage || 1, 1),
      Math.max(this.totalPages, 1),
    );

    // 翻页后交由调用方加载或切换对应页的数据。
    this.onChange = config.onChange || (() => {});

    // 移动端仅显示“当前页 / 总页数”；监听窗口跨越断点时重新渲染。
    this.mobileMediaQuery = window.matchMedia('(max-width: 768px)');
    this.handleViewportChange = () => this.render();
    this.mobileMediaQuery.addEventListener('change', this.handleViewportChange);

    // 调整该值即可控制桌面端页码、首末页和省略号的总数量。
    this.DESKTOP_PAGE_COUNT = 13; 

    this.render();
  }

  // 计算桌面端需要展示的分页项；返回数字页码或 'ellipsis'。
  getPageItems() {
    const total = this.totalPages;
    const current = this.currentPage;
    // 至少保留首、末页和两个省略号所需的 5 个位置。
    const visibleCount = Math.max(5, this.DESKTOP_PAGE_COUNT);
    // 首尾各保留最多 3 个页码，避免总页数很大时首尾信息不足。
    const edgeCount = Math.max(1, Math.min(1, Math.floor(visibleCount / 1)));
    // 首尾状态：连续页码 + 1 个省略号 + 对侧边缘页码。
    const leadingCount = visibleCount - edgeCount - 1;
    // 中间状态固定显示首末页和两个省略号，剩余位置围绕当前页展开。
    const middleCount = visibleCount - 4;

    if (total <= 1) return total === 1 ? [1] : [];

    // 总页数不超过上限时，无需省略任何页码。
    if (total <= visibleCount) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    // 计算以当前页为中心的中间页码窗口。
    const half = Math.floor(middleCount / 2);
    const start = current - half;
    const end = start + middleCount - 1;

    // 靠近首页：展示开头连续页码和结尾边缘页码。
    if (start <= 2) {
      const items = Array.from({ length: leadingCount }, (_, i) => i + 1);
      items.push('ellipsis');
      for (let i = total - edgeCount + 1; i <= total; i++) items.push(i);
      return items;
    }

    // 靠近末页：展示开头边缘页码和结尾连续页码。
    if (end >= total - 1) {
      const items = Array.from({ length: edgeCount }, (_, i) => i + 1);
      items.push('ellipsis');
      for (let i = total - leadingCount + 1; i <= total; i++) items.push(i);
      return items;
    }

    // 位于中间：固定首末页，当前页附近展示连续页码。
    return [1, 'ellipsis', ...Array.from({ length: end - start + 1 }, (_, i) => start + i), 'ellipsis', total];
  }

  // 将目标页码限制在合法范围内；页码未变化时不重复渲染或触发回调。
  setPage(page) {
    const next = Math.min(Math.max(page, 1), this.totalPages);
    if (next === this.currentPage) return;
    // 设置url page参数
    const url = new URL(window.location.href);
    url.searchParams.set('page', next);
    window.history.replaceState({}, '', url);
    this.currentPage = next;
    this.render();
    this.onChange(next);
    this.scrollToContent();
  }

  scrollToContent() {
    const top = this.productGrid.getBoundingClientRect().top + window.scrollY - 150;
    window.scrollTo({ top, behavior: 'smooth' });
  }

  // 外部数据刷新后同步总页数或当前页，复用同一个分页器实例。
  update({ totalPages, currentPage } = {}) {
    if (totalPages != null) this.totalPages = Math.max(0, totalPages);
    if (currentPage != null) {
      this.currentPage = Math.min(
        Math.max(currentPage, 1),
        Math.max(this.totalPages, 1),
      );
    }
    this.render();
  }

  render() {
    if (!this.container) return;

    // 每次按最新状态完整重建，避免保留过期的页码和点击事件。
    const existingPrevious = this.container.querySelector('[data-pagination-prev]');
    const existingNext = this.container.querySelector('[data-pagination-next]');
    if (!existingPrevious || !existingNext) return;
    if (this.totalPages <= 1) {
      this.container.classList.add('tw-hidden');
      return;
    }

    this.container.classList.remove('tw-hidden');

    // 只显示左右按钮，保留原有翻页逻辑。
    const previous = existingPrevious;
    const next = existingNext;
    const previousDisabled = this.currentPage <= 1;
    const nextDisabled = this.currentPage >= this.totalPages;
    previous.disabled = previousDisabled;
    next.disabled = nextDisabled;
    previous.classList.toggle('swiper-button-disabled', previousDisabled);
    next.classList.toggle('swiper-button-disabled', nextDisabled);
    previous.onclick = previousDisabled ? null : () => this.setPage(this.currentPage - 1);
    next.onclick = nextDisabled ? null : () => this.setPage(this.currentPage + 1);
    this.container.appendChild(previous);
    this.container.appendChild(next);
  }

  createNavButton(direction, disabled, onClick) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.disabled = disabled;
    btn.className = [
      'nw-arrow-button tw-size-[42px] tw-text-black',
    ].join(' ');
    if (direction === 'prev') btn.dataset.paginationPrev = '';
    else btn.dataset.paginationNext = '';
    btn.setAttribute(
      'aria-label',
      direction === 'prev' ? (this.labels.previous || 'Previous page') : (this.labels.next || 'Next page'),
    );
    btn.innerHTML = direction === 'prev'
      ? '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3.6948 6.50541C3.42137 6.77885 3.42137 7.22291 3.6948 7.49635L7.8948 11.6963C8.16824 11.9698 8.6123 11.9698 8.88574 11.6963C9.15918 11.4229 9.15918 10.9788 8.88574 10.7054L5.18012 6.99978L8.88355 3.29416C9.15699 3.02072 9.15699 2.57666 8.88355 2.30322C8.61012 2.02979 8.16605 2.02979 7.89262 2.30322L3.69262 6.50322L3.6948 6.50541Z" fill="#161616"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M10.3052 6.50541C10.5786 6.77885 10.5786 7.22291 10.3052 7.49635L6.1052 11.6963C5.83176 11.9698 5.3877 11.9698 5.11426 11.6963C4.84082 11.4229 4.84082 10.9788 5.11426 10.7054L8.81988 6.99978L5.11645 3.29416C4.84301 3.02072 4.84301 2.57666 5.11645 2.30322C5.38988 2.02979 5.83395 2.02979 6.10738 2.30322L10.3074 6.50322L10.3052 6.50541Z" fill="#161616"/></svg>';

    // 禁用态不绑定事件，防止首页继续向前或末页继续向后翻。
    return btn;
  }

  createPageButton(page, isActive) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = String(page);
    btn.setAttribute(
      'aria-label',
      (this.labels.page || 'Page __NUMBER__').replace('__NUMBER__', String(page)),
    );
    btn.setAttribute('aria-current', isActive ? 'page' : 'false');

    const base = [
      'tw-flex tw-items-center tw-justify-center',
      'tw-min-w-6 tw-h-6 tw-px-1',
      'tw-text-sm tw-leading-none tw-font-normal',
      'tw-border-0 tw-cursor-pointer',
      'tw-transition-colors',
    ];

    if (isActive) {
      btn.className = [...base, 'tw-rounded-full tw-bg-brand tw-text-white tw-pointer-events-none'].join(' ');
    } else {
      btn.className = [...base, 'tw-text-[#161616] hover:tw-text-brand'].join(' ');
      btn.addEventListener('click', () => this.setPage(page));
    }

    return btn;
  }

  createMobilePageIndicator() {
    const indicator = document.createElement('span');
    indicator.className = 'tw-text-sm tw-leading-none tw-font-normal tw-text-[#161616] tw-min-w-[54px] tw-text-center';
    indicator.textContent = `${this.currentPage} / ${this.totalPages}`;
    // 读屏软件在翻页时会温和地播报新的页码进度。
    indicator.setAttribute('aria-live', 'polite');
    return indicator;
  }

  createEllipsis() {
    const span = document.createElement('span');
    span.className = 'tw-flex tw-items-center tw-justify-center tw-min-w-6 tw-h-6 tw-text-sm tw-text-[#161616] tw-pointer-events-none';
    span.textContent = '...';
    span.setAttribute('aria-hidden', 'true');
    return span;
  }
}

export { NavimowPagination };
